import React, { createContext, useContext, useState, useEffect } from 'react';

type TOCItem = {
  title: string;
  anchor: string;
  level: 'h2' | 'h3';
};

type TOCContextType = {
  items: TOCItem[];
  activeHeading: string | null;
  registerHeading: (title: string, anchor: string, level: 'h2' | 'h3') => void;
  clearHeadings: () => void;
};

const TOCContext = createContext<TOCContextType | undefined>(undefined);

export const TOCProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [items, setItems] = useState<TOCItem[]>([]);
  const [activeHeading, setActiveHeading] = useState<string | null>(null);

  const registerHeading = (
    title: string,
    anchor: string,
    level: 'h2' | 'h3'
  ) => {
    setItems(prev => {
      // Check if heading already exists (avoid duplicates)
      const exists = prev.some(item => item.anchor === anchor);
      if (exists) return prev;

      // Add new heading
      return [...prev, { title, anchor, level }];
    });
  };

  const clearHeadings = () => {
    setItems([]);
  };

  // Scroll tracking effect
  useEffect(() => {
    if (items.length === 0) return;

    const handleScroll = () => {
      // Get all heading elements
      const headingElements = items
        .map(item => {
          const element = document.getElementById(item.anchor);
          return element
            ? { element, anchor: item.anchor, level: item.level }
            : null;
        })
        .filter(Boolean) as Array<{
        element: HTMLElement;
        anchor: string;
        level: 'h2' | 'h3';
      }>;

      if (headingElements.length === 0) return;

      // Calculate which heading is currently active
      const scrollPosition = window.scrollY + 100; // 100px offset for better UX

      // Find the heading that's currently in view
      let currentHeading = headingElements[0].anchor;

      for (let i = headingElements.length - 1; i >= 0; i--) {
        const { element, anchor } = headingElements[i];
        if (element.offsetTop <= scrollPosition) {
          currentHeading = anchor;
          break;
        }
      }

      // Special case: if we're near the bottom of the page, highlight the last heading
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      if (scrollPosition + windowHeight >= documentHeight - 100) {
        currentHeading = headingElements[headingElements.length - 1].anchor;
      }

      setActiveHeading(currentHeading);
    };

    // Initial call
    handleScroll();

    // Add scroll listener with throttling
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll);

    // Also listen for resize events in case layout changes
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [items]);

  return (
    <TOCContext.Provider
      value={{ items, activeHeading, registerHeading, clearHeadings }}
    >
      {children}
    </TOCContext.Provider>
  );
};

export const useTOC = () => {
  const context = useContext(TOCContext);
  if (context === undefined) {
    throw new Error('useTOC must be used within a TOCProvider');
  }
  return context;
};
